// FLAG:useDocuAi — review-component binding: turn the per-field metadata DocuAI
// carries in the contract envelope (documentInvoiceDetails.fieldMeta) into a
// per-field "should the user check this?" state for the review UI. Replaces the
// old pure-confidence hack (extractionConfidence + hardcoded thresholds) — that
// map is still read as a fallback until every field carries fieldMeta.

// Graded per-field verdict DocuAI stamps on every leaf (schema 1.2.0):
// 'error'   — hard check failed ("value looks wrong") → red border treatment
// 'warning' — soft "please check" (low confidence, distrusted) → yellow border
// 'info'    — machine-assigned/derived value that PASSED its cross-check (e.g.
//             the document's single VAT rate assigned to rate-less positions)
//             → marker/tooltip, no border
// 'ok'      — checked / nothing to show
export type ValidationState = 'ok' | 'info' | 'warning' | 'error'

export interface FieldMetaEntry {
  confidence?: number | null
  source?: 'llm' | 'rule' | 'template' | 'xml' | 'human' | null
  // DocuAI's finalizer stamps the state on every leaf, so it is normally
  // present; null/absent only on a legacy envelope (reads stay absent-tolerant).
  validationState?: ValidationState | null
  validationNote?: string | null
  // stable machine code of the rule/mechanism behind a non-'ok' state
  // (grouping/help links, e.g. 'iban_mod97', 'line_tax_assigned')
  validationCode?: string | null
}

// §3.5 XML per-field lock: per-line meta the server attaches under
// fieldMeta.positions (contractToAnalysis), aligned 1:1 with the built
// positions — fieldMeta.positions[i] describes position i.
export interface PositionFieldMeta {
  vatPercent?: FieldMetaEntry | null
  price?: FieldMetaEntry | null
  description?: FieldMetaEntry | null
}

export type PositionsFieldMeta = Array<PositionFieldMeta | null>

export type FieldMetaMap = Record<string, FieldMetaEntry> & {
  // Not a FieldMetaEntry — an array of per-line maps. Read it only through
  // isPositionValueXmlLocked; getFieldValidation/getFlaggedFields ignore it.
  positions?: PositionsFieldMeta | null
}

// Mirror of api/src/routes/file/helpers/extractionConfidence.js — a value at or
// below its threshold is still shown (never hidden) but marked "please check".
export const DEFAULT_CONFIDENCE_THRESHOLD = 0.9

// The visual treatment a field should get, derived from the graded verdict. This is
// the ONE place that maps verdict → colour, so every consumer agrees: 'error' is red,
// 'warning' is the yellow review color, everything else is unmarked. 'info' deliberately
// yields null — a machine-assigned value that PASSED its cross-check gets a marker or
// tooltip, never a border.
export type FieldTreatment = 'error' | 'warning' | null

export interface FieldValidationState {
  // the graded verdict as delivered — drives the visual treatment directly
  // ('error' → red border, 'warning' → yellow border, 'info' → marker/tooltip,
  // 'ok' → nothing); null on a legacy envelope without per-field states
  state: ValidationState | null
  // what the UI should actually paint (see FieldTreatment). Prefer this over
  // `uncertain`, which cannot tell a hard failure from a soft "please check".
  treatment: FieldTreatment
  // a validation check flagged the value (state 'error' | 'warning')
  flagged: boolean
  // a check has spoken and the value is trustworthy (state 'ok' | 'info' —
  // an 'info' assignment passed its arithmetic cross-check)
  checkedOk: boolean
  // the field should draw the reviewer's attention: flagged, or (no check yet
  // but) extraction confidence is at/below the threshold
  uncertain: boolean
  note?: string | null
  confidence?: number | null
  source?: string | null
}

export const getFieldValidation = (
  fieldMeta: FieldMetaMap | null | undefined,
  extractionConfidence: Record<string, number> | null | undefined,
  field: string,
  threshold: number = DEFAULT_CONFIDENCE_THRESHOLD,
): FieldValidationState => {
  const meta = fieldMeta?.[field]
  const state = meta?.validationState ?? null
  const flagged = state === 'error' || state === 'warning'
  const checkedOk = state === 'ok' || state === 'info'
  // real confidence from fieldMeta, else the legacy extractionConfidence map
  const confidence = typeof meta?.confidence === 'number'
    ? meta.confidence
    : (typeof extractionConfidence?.[field] === 'number' ? extractionConfidence[field] : null)
  // A flagged field is always uncertain regardless of confidence. A checked-OK
  // field is trusted even at low confidence (a check has spoken). Otherwise fall
  // back to the confidence threshold.
  const lowConfidence = typeof confidence === 'number' && confidence <= threshold
  const uncertain = flagged || (!checkedOk && lowConfidence)
  // A verdict always wins. Without one (legacy envelope), a low-confidence value is a
  // soft "please check" — yellow, never red: nothing has actually failed a check.
  const treatment: FieldTreatment = state === 'error'
    ? 'error'
    : (state === 'warning' || (state === null && lowConfidence) ? 'warning' : null)
  return {
    state,
    treatment,
    flagged,
    checkedOk,
    uncertain,
    note: meta?.validationNote ?? null,
    confidence,
    source: meta?.source ?? null,
  }
}

// FLAG:useDocuAi — one recognized payee account as ingested from the DocuAI contract
// (schema 1.7.0 `paymentDetails.payeeAccounts` → documentInvoiceDetails.payeeAccounts),
// index-aligned with payeeIbans. `currency` is the code the document PRINTED with this
// account ("IBAN: DE34 … (USD)"), never an inference; the validation triple is the verdict
// DocuAI stamped on it (today only `payee_account_currency` — this account settles in a
// currency the invoice does not).
export interface PayeeAccountMeta {
  iban?: string | null
  bic?: string | null
  bankName?: string | null
  currency?: string | null
  validationState?: ValidationState | null
  validationCode?: string | null
  validationNote?: string | null
}

// Routed through getFieldValidation so an account card gets the SAME verdict → colour
// mapping as every scalar field. It cannot go through fieldMeta: that map is flat and has
// no per-account slot, which is exactly why a non-selected card could carry no mark at all.
export const getPayeeAccountValidation = (
  account: PayeeAccountMeta | null | undefined,
): FieldValidationState => getFieldValidation(
  {
    currency: {
      validationState: account?.validationState ?? null,
      validationNote: account?.validationNote ?? null,
      validationCode: account?.validationCode ?? null,
    },
  },
  null,
  'currency',
)

// The same graded rule for ONE line's field (fieldMeta.positions[i]). Positions carry no
// legacy extractionConfidence map — a line's confidence, if any, rides on its own leaf.
export const getPositionFieldValidation = (
  positionsFieldMeta: PositionsFieldMeta | null | undefined,
  index: number,
  field: keyof PositionFieldMeta,
): FieldValidationState => getFieldValidation(
  (positionsFieldMeta?.[index] ?? null) as FieldMetaMap | null,
  null,
  field as string,
)

// §3.5 XML lock: on an XML e-invoice the mapped values are legally binding and
// read-only in the review. The whole-doc signal (reviewSource.source === 'xml',
// with metaData.invoiceAnalysisSource as the pre-reviewSource fallback) locks
// every mapped field — mirroring the server's enforceXmlLockedFields backstop —
// and a per-field source === 'xml' locks a field even without it.
export const isValueXmlLocked = (
  meta: FieldMetaEntry | null | undefined,
  xmlLockedDocument: boolean,
): boolean => xmlLockedDocument || meta?.source === 'xml'

export const isPositionValueXmlLocked = (
  positionsFieldMeta: PositionsFieldMeta | null | undefined,
  index: number,
  field: keyof PositionFieldMeta,
  xmlLockedDocument: boolean,
): boolean => xmlLockedDocument || positionsFieldMeta?.[index]?.[field]?.source === 'xml'

// Every field a check flagged — for a compact "please check these" summary.
// `state` distinguishes the red (error) from the yellow (warning) entries.
export const getFlaggedFields = (
  fieldMeta: FieldMetaMap | null | undefined,
): Array<{ field: string, note: string | null, state: ValidationState }> =>
  Object.entries(fieldMeta || {})
    .filter((entry): entry is [string, FieldMetaEntry] => {
      const meta = entry[1] as FieldMetaEntry | null | undefined
      return meta?.validationState === 'error' || meta?.validationState === 'warning'
    })
    .map(([field, meta]) => ({
      field,
      note: meta.validationNote ?? null,
      state: meta.validationState as ValidationState,
    }))
